import { Typography } from '@mui/material';

import {
  InfoReviewFieldLabel,
  InfoReviewFieldValue,
} from '@/components/Form/Form.styled';
import { Divider } from '@/components/Layout/Divider';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import {
  selectAdobeSignFormFeature,
  selectMailingAddressFeature,
} from '@/redux/configs';

import { Language } from '@/types/i18n';
import { User } from '@/types/user';

import {
  getMailingAddressFromUser,
  getPermanentAddressFromUser,
} from '@/utils/address';
import { getCompleteAddress } from '@/utils/places';
import { getDisplayedPhone } from '@/utils/string';
import { getCitizenships, isRemediationCustomer } from '@/utils/user';

import { InfoReviewDetailCard } from './styled';

type ContactDetailCardProps = {
  user: User;
};

export function ContactDetailCard({ user }: Readonly<ContactDetailCardProps>) {
  const { t, country } = useTranslate();

  const isMailingAddressEnabled = useAppSelector(selectMailingAddressFeature);
  const isAdobeSignFormEnabled = useAppSelector(selectAdobeSignFormFeature);
  const isEnhancedTaxSectionVisible = !isAdobeSignFormEnabled;
  const language = (user.language as Language) || Language.English;
  return (
    <InfoReviewDetailCard>
      <Typography variant="h5" paddingBottom={1}>
        {t('reviewPersonalDetails.contactDetails.title')}
      </Typography>
      <Divider width={'100%'} />
      <InfoReviewFieldLabel>
        {t('reviewPersonalDetails.contactDetails.field.phoneNumber')}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {getDisplayedPhone(user.phoneNumber)}
      </InfoReviewFieldValue>
      {user.citizenship && (
        <>
          <InfoReviewFieldLabel>
            {t('reviewPersonalDetails.contactDetails.field.citizenship')}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {getCitizenships(user, language)}
          </InfoReviewFieldValue>
        </>
      )}
      <AddressInfo user={user} />
      {isMailingAddressEnabled && (
        <>
          <InfoReviewFieldLabel>
            {t('reviewPersonalDetails.contactDetails.field.countryOfResidence')}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {user.residenceCountry &&
              (country[user.residenceCountry] || user.residenceCountry)}
          </InfoReviewFieldValue>
        </>
      )}

      {!isEnhancedTaxSectionVisible && (
        <>
          <InfoReviewFieldLabel>
            {t('reviewPersonalDetails.contactDetails.field.taxResidence')}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {country[user.taxResidences as string] || user.taxResidences}
          </InfoReviewFieldValue>
        </>
      )}
    </InfoReviewDetailCard>
  );
}

type AddressInfoProps = {
  user?: User;
};
const AddressInfo = ({ user }: Readonly<AddressInfoProps>) => {
  const { t } = useTranslate();
  const shouldAddMailingAddress = useAppSelector(selectMailingAddressFeature);

  if (!user) {
    return;
  }

  const permanentAddress = getPermanentAddressFromUser(user);
  const language = (user.language as Language) || Language.English;

  if (isRemediationCustomer(user)) {
    return (
      <>
        <InfoReviewFieldLabel>
          {t('reviewPersonalDetails.contactDetails.field.permanentAddress')}
        </InfoReviewFieldLabel>
        <InfoReviewFieldValue>
          {getCompleteAddress(
            permanentAddress ?? user.addressDetailed,
            language,
          )}
        </InfoReviewFieldValue>
      </>
    );
  }

  if (shouldAddMailingAddress) {
    const mailingAddress = getMailingAddressFromUser(user);

    return (
      <>
        <InfoReviewFieldLabel>
          {t('reviewPersonalDetails.contactDetails.field.permanentAddress')}
        </InfoReviewFieldLabel>
        {permanentAddress && (
          <InfoReviewFieldValue>
            {getCompleteAddress(permanentAddress, language)}
          </InfoReviewFieldValue>
        )}
        <InfoReviewFieldLabel>
          {t('reviewPersonalDetails.contactDetails.field.mailingAddress')}
        </InfoReviewFieldLabel>
        {mailingAddress && (
          <InfoReviewFieldValue>
            {getCompleteAddress(mailingAddress, language)}
          </InfoReviewFieldValue>
        )}
      </>
    );
  }

  return (
    <>
      <InfoReviewFieldLabel>
        {t('reviewPersonalDetails.contactDetails.field.mailingAddress')}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {getCompleteAddress(user.addressDetailed, language)}
      </InfoReviewFieldValue>
    </>
  );
};
